// @ts-nocheck
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { EventData } from "@/types/events";
import { SubSection, FieldLabel, inputClass } from "./SectionWrapper";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ExternalLink, UserPlus, BedDouble } from "lucide-react";

interface Props {
  event: EventData;
  updateEvent: (updates: Partial<EventData>) => void;
}

interface HotelPartner {
  user_id: string;
  email: string;
  name: string | null;
  events_managed: number;
}

export function StayToPlaySubsection({ event, updateEvent }: Props) {
  const { user } = useAuth();
  const [assignment, setAssignment] = useState<any>(null);
  const [partners, setPartners] = useState<HotelPartner[]>([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load assignment
  useEffect(() => {
    if (!event.id) return;
    (async () => {
      const { data } = await (supabase as any)
        .from("hotel_event_assignments")
        .select("*")
        .eq("event_id", event.id)
        .maybeSingle();
      setAssignment(data || {});
    })();
  }, [event.id]);

  // Load hotel_partner users
  useEffect(() => {
    if (event.stay_to_play_mode !== "partner_managed") return;
    (async () => {
      const { data: roles } = await (supabase as any)
        .from("user_roles")
        .select("user_id")
        .eq("role", "hotel_partner");
      if (!roles?.length) {
        setPartners([]);
        return;
      }
      const ids = roles.map((r: any) => r.user_id);
      const { data: profs } = await (supabase as any)
        .from("profiles")
        .select("user_id, email, name")
        .in("user_id", ids);
      const { data: counts } = await (supabase as any)
        .from("hotel_event_assignments")
        .select("hotel_partner_user_id")
        .in("hotel_partner_user_id", ids);
      const cmap: Record<string, number> = {};
      (counts || []).forEach((c: any) => {
        cmap[c.hotel_partner_user_id] = (cmap[c.hotel_partner_user_id] || 0) + 1;
      });
      setPartners(
        (profs || []).map((p: any) => ({
          user_id: p.user_id,
          email: p.email,
          name: p.name,
          events_managed: cmap[p.user_id] || 0,
        }))
      );
    })();
  }, [event.stay_to_play_mode]);

  const persistAssignment = useCallback(
    async (patch: Record<string, any>) => {
      if (!event.id) return;
      setSaving(true);
      const merged = { ...(assignment || {}), ...patch, event_id: event.id };
      setAssignment(merged);
      const { error } = await (supabase as any)
        .from("hotel_event_assignments")
        .upsert(
          {
            event_id: event.id,
            hotel_partner_user_id: merged.hotel_partner_user_id ?? null,
            booking_url: merged.booking_url ?? null,
            reservetravel_group_id: merged.reservetravel_group_id ?? null,
            reservetravel_group_url: merged.reservetravel_group_url ?? null,
            group_rate_expiry: merged.group_rate_expiry || null,
            notes: merged.notes ?? null,
            assigned_by: merged.assigned_by ?? user?.id ?? null,
          },
          { onConflict: "event_id" }
        );
      if (error) toast.error("Hotel save failed: " + error.message);
      setSaving(false);
    },
    [assignment, event.id, user?.id]
  );

  const mode = event.stay_to_play_mode === "partner_managed" ? "partner_managed" : "simple_url";

  return (
    <SubSection title="STAY TO PLAY">

      <p className="text-xs text-muted-foreground -mt-1 mb-3">
        Hotel booking and accommodation requirements for this event.
      </p>

      <label className="flex items-center justify-between p-3 border border-border rounded-lg">
        <span className="text-sm font-medium">This event uses stay-to-play</span>
        <input
          type="checkbox"
          checked={!!event.stay_to_play_enabled}
          onChange={(e) => updateEvent({ stay_to_play_enabled: e.target.checked })}
          className="rounded"
        />
      </label>

      {event.stay_to_play_enabled && (
        <div className="mt-4 space-y-4 pl-4 border-l-2 border-primary/20">
          <label className="flex items-start justify-between gap-3 p-3 border border-border rounded-lg">
            <div>
              <div className="text-sm font-medium">Stay-to-play is required for all teams</div>
              <div className="text-xs text-muted-foreground mt-1">
                When required, teams must confirm they're booking through your partner hotels at registration.
              </div>
            </div>
            <input
              type="checkbox"
              checked={!!event.stay_to_play_required}
              onChange={(e) => updateEvent({ stay_to_play_required: e.target.checked })}
              className="rounded mt-1"
            />
          </label>

          {/* Local team radius */}
          <div>
            <FieldLabel>Local team radius (miles)</FieldLabel>
            <input
              type="number"
              min={1}
              inputMode="numeric"
              value={event.hotel_exempt_radius_miles ?? ""}
              onChange={(e) =>
                updateEvent({
                  hotel_exempt_radius_miles:
                    e.target.value === "" ? null : Math.max(1, Number(e.target.value) || 0),
                })
              }
              placeholder="70"
              className={inputClass}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Teams whose home ZIP is within this distance of the main venue are automatically
              marked exempt and won't get hotel reminders. Leave blank to turn this off. Saving
              re-applies it to all registered teams automatically.
            </p>
          </div>

          {/* Mode radio cards */}
          <div className="space-y-2">
            <FieldLabel>Mode</FieldLabel>
            {[
              { id: "simple_url", label: "Simple URL", desc: "Paste a booking link, no other setup needed" },
              { id: "partner_managed", label: "Hotel Partner", desc: "Assign a hotel partner who manages properties, RFPs, bookings" },
            ].map((opt) => (
              <label
                key={opt.id}
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  mode === opt.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                }`}
              >
                <input
                  type="radio"
                  name="stay_mode"
                  value={opt.id}
                  checked={mode === opt.id}
                  onChange={() => updateEvent({ stay_to_play_mode: opt.id as any })}
                  className="mt-1"
                />
                <div>
                  <div className="text-sm font-medium">{opt.label}</div>
                  <div className="text-xs text-muted-foreground">{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>

          {/* Mode-specific fields */}
          {mode === "simple_url" && (
            <div className="space-y-3">
              <div>
                <FieldLabel>Hotel booking URL</FieldLabel>
                <input
                  type="url"
                  value={event.hotel_booking_url || ""}
                  onChange={(e) => {
                    updateEvent({ hotel_booking_url: e.target.value });
                  }}
                  onBlur={() => persistAssignment({ booking_url: event.hotel_booking_url })}
                  placeholder="https://..."
                  className={inputClass}
                />
              </div>
              <NotesField event={event} updateEvent={updateEvent} />
            </div>
          )}


          {mode === "partner_managed" && (
            <div className="space-y-3">
              <div>
                <FieldLabel>Assigned partner</FieldLabel>
                {partners.length === 0 ? (
                  <div className="text-xs text-muted-foreground p-3 border border-dashed rounded-lg">
                    No hotel partners yet.
                  </div>
                ) : (
                  <select
                    value={assignment?.hotel_partner_user_id || ""}
                    onChange={(e) => persistAssignment({ hotel_partner_user_id: e.target.value || null })}
                    className={inputClass}
                  >
                    <option value="">— Select a partner —</option>
                    {partners.map((p) => (
                      <option key={p.user_id} value={p.user_id}>
                        {(p.name || p.email) + ` (${p.events_managed} events managed)`}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  onClick={() => setInviteOpen(true)}
                  className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <UserPlus className="w-3 h-3" /> Invite a hotel partner →
                </button>
              </div>

              {assignment?.hotel_partner_user_id && (
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-sm">
                  <div className="font-medium">
                    {partners.find((p) => p.user_id === assignment.hotel_partner_user_id)?.name ||
                      partners.find((p) => p.user_id === assignment.hotel_partner_user_id)?.email ||
                      "Partner"}{" "}
                    is managing this event
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {/* TODO: live stats from hotel_properties / hotel_bookings */}
                    Properties, bookings, and revenue will appear here once they're added.
                  </div>
                  <a
                    href={`/admin-dashboard/hotels/${event.id}`}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                  >
                    View partner dashboard <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}

              <div>
                <FieldLabel>Booking URL fallback</FieldLabel>
                <input
                  type="url"
                  value={event.hotel_booking_url || ""}
                  onChange={(e) => updateEvent({ hotel_booking_url: e.target.value })}
                  onBlur={() => persistAssignment({ booking_url: event.hotel_booking_url })}
                  placeholder="https://... (shown to teams if partner setup is incomplete)"
                  className={inputClass}
                />
              </div>
              <NotesField event={event} updateEvent={updateEvent} />
            </div>
          )}

          {saving && <div className="text-xs text-muted-foreground">Saving…</div>}
        </div>
      )}

      <InviteHotelPartnerDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        eventId={event.id || null}
      />
    </SubSection>
  );
}

function NotesField({ event, updateEvent }: Props) {
  return (
    <div>
      <FieldLabel>Notes for teams</FieldLabel>
      <textarea
        value={event.stay_to_play_notes || ""}
        onChange={(e) => updateEvent({ stay_to_play_notes: e.target.value })}
        rows={3}
        placeholder="e.g. Cut-off date is two weeks before the event. Ask for the rate by mentioning [event name]."
        className={inputClass + " resize-none"}
      />
    </div>
  );
}

function InviteHotelPartnerDialog({
  open,
  onOpenChange,
  eventId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  eventId: string | null;
}) {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [autoAssign, setAutoAssign] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setEmail("");
    setName("");
    setPhone("");
    setCompany("");
    setAutoAssign(true);
  };

  const handleSubmit = async () => {
    if (!email || !name) {
      toast.error("Email and name are required");
      return;
    }
    setSubmitting(true);
    // Stub: insert admin_invitations row with role hotel_partner
    const tokenHash = crypto.randomUUID().replace(/-/g, "");
    const expiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
    const { error } = await (supabase as any).from("admin_invitations").insert({
      invitee_email: email.trim().toLowerCase(),
      invitee_name: name,
      invitee_phone: phone || null,
      role: "hotel_partner",
      personal_message: company ? `Company: ${company}` : null,
      invited_by: user?.id,
      token_hash: tokenHash,
      status: "pending",
      expires_at: expiresAt,
    });
    if (error) {
      toast.error("Invite failed: " + error.message);
      setSubmitting(false);
      return;
    }
    // TODO: send email with invitation link
    toast.success("Invite created. Email send is a TODO — share the acceptance link manually for now.");
    setSubmitting(false);
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a hotel partner</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <FieldLabel>Email *</FieldLabel>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
          </div>
          <div>
            <FieldLabel>Full name *</FieldLabel>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <FieldLabel>Phone (optional)</FieldLabel>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <FieldLabel>Company / Hotel group (optional)</FieldLabel>
            <Input value={company} onChange={(e) => setCompany(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoAssign}
              onChange={(e) => setAutoAssign(e.target.checked)}
            />
            Assign to this event automatically (after they accept)
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Sending…" : "Send invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
